import React from 'react';
import { Dialog, Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';

interface ExampleImageModalProps {
  open: boolean;
  images: string[];
  captions?: string[];
  stepTitle: string;
  onClose: () => void;
  onTakePhoto: () => void;
}

const ExampleImageModal: React.FC<ExampleImageModalProps> = ({
  open,
  images,
  captions = [],
  stepTitle,
  onClose,
  onTakePhoto,
}) => {
  const [currentSlide, setCurrentSlide] = React.useState<number>(0);

  React.useEffect(() => {
    if (open) setCurrentSlide(0);
  }, [open]);

  if (!images.length) return null;

  const hasMultiple = images.length > 1;
  const caption = captions[currentSlide] || stepTitle;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      data-testid="example-modal"
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          height: '584px',
          maxHeight: '90vh',
          borderRadius: '24px 24px 0 0',
          overflow: 'hidden',
          backgroundColor: '#000',
        },
      }}
    >
      {hasMultiple && (
        <Box
          sx={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            zIndex: 20,
            padding: '8px 16px',
            background: 'rgba(0,0,0,0.75)',
            borderRadius: '8px',
            color: '#FFFFFF',
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontWeight: 500,
          }}
          data-testid="example-counter"
        >
          {currentSlide + 1}/{images.length}
        </Box>
      )}

      <IconButton
        onClick={onClose}
        data-testid="example-close"
        sx={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 20,
          background: 'rgba(0,0,0,0.75)',
          color: '#FFFFFF',
          '&:hover': { background: 'rgba(0,0,0,0.85)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={images[currentSlide]}
          alt={caption}
          data-testid="example-image"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />

        {hasMultiple && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: '150px',
              zIndex: 25,
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                data-testid="example-dot"
                sx={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  background:
                    idx === currentSlide
                      ? 'rgba(255,255,255,0.9)'
                      : 'rgba(0,0,0,0.6)',
                }}
              />
            ))}
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            minHeight: '144px',
            padding: '24px 16px 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '24px',
            background:
              'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0) 100%)',
          }}
        >
          <Box
            sx={{
              color: '#FFFFFF',
              fontFamily: 'Poppins',
              fontSize: '18px',
              fontWeight: 500,
              textAlign: 'center',
              textShadow: '0 2px 8px rgba(0,0,0,.5)',
              maxWidth: '90%',
            }}
          >
            {caption}
          </Box>
          <Button
            onClick={onTakePhoto}
            data-testid="example-take-photo"
            startIcon={<CameraAltOutlinedIcon />}
            sx={{
              width: '100%',
              maxWidth: '320px',
              height: '56px',
              borderRadius: '28px',
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontWeight: 600,
              background: 'rgba(255,255,255,0.92)',
              color: '#1C1C1C',
              boxShadow: '0 8px 24px rgba(0,0,0,.35)',
              '&:hover': { background: '#FFFFFF' },
            }}
          >
            Tirar foto
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ExampleImageModal;
